import { measure } from '@/components/Analytics/Analytics';
import AudioPlayer from '@/components/AudioPlayer/AudioPlayer';
import BottomSheet from '@/components/BottomSheet/BottomSheet';
import CloseIcon from '@/components/Icon/CloseIcon';
import DeleteIcon from '@/components/Icon/DeleteIcon';
import DownloadIcon from '@/components/Icon/DownloadIcon';
import MenuIcon from '@/components/Icon/MenuIcon';
import IconButton from '@/components/IconButton/IconButton';
import LabelBackground from '@/components/LabelBackground/LabelBackground';
import useCards, {
	CardMediaWithType,
	getAllCardMediaWithTypes,
	useCardMainMedia,
} from '@/hooks/cards/useCards';
import useIsNative from '@/hooks/native/useIsNative';
import useNativeMessages from '@/hooks/native/useNativeMessages';
import { SpeciesAttributes, StrapiEntry } from '@/lib/apiTypes';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';

// Random rotation between -5 and 5 degrees
const randomRotation = () => Math.random() * 6 - 3;

type LoadedCardMedia = CardMediaWithType & {
	src: string;
	rotation: number;
};

const UserMediaGallery = ({
	species,
	mainUserPhoto,
	onClose,
}: {
	species: StrapiEntry<SpeciesAttributes>;
	mainUserPhoto: ReturnType<typeof useCardMainMedia>['mainUserPhoto'];
	onClose: () => void;
}) => {
	const isNative = useIsNative();
	const { sendNativeMessage } = useNativeMessages();

	const reduceMotion = useReducedMotion();

	const {
		getCardBySpeciesId,
		readPhoto,
		readAudio,
		deleteMediaItem: deleteFromCard,
		getMediaItemPath,
		getMediaItemExtension,
		getMediaItemMimeType,
	} = useCards();
	const card = useMemo(
		() => getCardBySpeciesId(species.id),
		[getCardBySpeciesId, species.id],
	);
	const [media, setMedia] = useState<LoadedCardMedia[]>(
		// Prefill with mainUserPhoto if it exists
		card?.photos.length && !!mainUserPhoto
			? [
					{
						...card.photos[0],
						type: 'photo',
						src: mainUserPhoto,
						rotation: randomRotation(),
					},
				]
			: [],
	);

	// Load full media items
	useEffect(() => {
		if (!card) return;
		const media = getAllCardMediaWithTypes(card).map(async (mediaItem) => {
			const src =
				mediaItem.type === 'photo'
					? await readPhoto(mediaItem.id)
					: await readAudio(mediaItem.id);
			return { ...mediaItem, src, rotation: randomRotation() };
		});
		Promise.all(media).then(setMedia);
	}, [card, readAudio, readPhoto]);

	// Share a media item
	const isShareAvailable = isNative || navigator.share !== undefined;
	const shareMediaItem = useCallback(
		async (mediaItem: LoadedCardMedia) => {
			measure(
				`species-gallery-share-${species.id}-${species.attributes.slug}`,
				'download',
			);

			// If is native, use native functions
			if (isNative) {
				sendNativeMessage(
					'share.file',
					getMediaItemPath(mediaItem.id, mediaItem.type),
				);
				return;
			}

			// If not native, use browser share
			const imageBlob = await fetch(mediaItem.src).then((res) =>
				res.blob(),
			);

			navigator.share({
				title: `Mijn waarneming van een ${species.attributes.titleLong} - Het Klokhuis SoortSafari`,
				text: `Waarneming van ${
					mediaItem.identifiedAs
				} gemaakt op ${new Date(mediaItem.timestamp).toLocaleDateString(
					'nl-NL',
				)} - Het Klokhuis SoortSafari`,
				files: [
					new File(
						[imageBlob],
						`SoortSafari-${
							species.attributes.titleShort
						}.${getMediaItemExtension(mediaItem.type)}`,
						{
							type: getMediaItemMimeType(mediaItem.type),
						},
					),
				],
			});
		},
		[
			species.id,
			species.attributes.slug,
			species.attributes.titleLong,
			species.attributes.titleShort,
			isNative,
			getMediaItemExtension,
			getMediaItemMimeType,
			sendNativeMessage,
			getMediaItemPath,
		],
	);

	const deleteMediaItem = useCallback(
		(mediaItem: LoadedCardMedia) => {
			measure(
				`species-gallery-delete-${species.id}-${species.attributes.slug}`,
				'exit',
			);

			deleteFromCard(mediaItem.id);
			onClose();
		},
		[deleteFromCard, onClose, species.attributes.slug, species.id],
	);

	if (!card) return null;

	return (
		<Dialog
			open
			onClose={onClose}
			className="fixed inset-0 size-full bg-gray-950/60 text-gray-50 backdrop-blur-md">
			<DialogPanel className="fixed inset-0 size-full">
				<div className="fixed start-4 top-4 z-20 portrait:start-1/2 portrait:top-8 portrait:-translate-x-1/2 portrait:xs:top-12">
					<IconButton
						onClick={onClose}
						aria-label="Sluiten"
						title="Sluiten"
						icon={<CloseIcon />}
					/>
				</div>
				<DialogTitle className="sr-only">Jouw verzameling</DialogTitle>
				<motion.div
					layoutId={
						reduceMotion ? undefined : `UserMedia-${card.id}-${0}`
					}
					className="h-full snap-center overflow-y-auto overflow-x-clip py-12 xxs:snap-y xxs:snap-mandatory xxs:py-32">
					<ol className="grid gap-8 landscape:gap-12">
						{media.map((mediaItem, index) => (
							<li
								key={`${mediaItem.id}-${index}`}
								className="relative flex snap-center snap-always flex-col-reverse items-center justify-start p-4 xxs:max-h-[70vh]">
								<div className="relative grid shrink-0 items-start justify-start">
									<h2 className="relative flex gap-4 px-4 pb-2 pt-3 font-brand text-2xl text-gray-950/90 md:text-3xl lg:text-4xl">
										<LabelBackground className="text-gray-50" />
										<p className="relative grow">
											{mediaItem.identifiedAs}
											<span className="block text-xl">
												{new Date(
													mediaItem.timestamp,
												).toLocaleDateString('nl-NL', {
													weekday: 'long',
													year: 'numeric',
													month: 'long',
													day: 'numeric',
												})}
											</span>
										</p>
										<BottomSheet
											className="relative my-auto"
											triggerButton={{
												content: (
													<MenuIcon className="size-8" />
												),
												label: 'Menu met meer opties',
											}}
											options={[
												{
													label: 'Delen naar andere app',
													icon: <DownloadIcon />,
													isVisible: isShareAvailable,
													onClick: () => {
														shareMediaItem(
															mediaItem,
														);
													},
												},
												{
													label: 'Verwijderen',
													icon: <DeleteIcon />,
													onClick: () =>
														deleteMediaItem(
															mediaItem,
														),
													confirmation: {
														message:
															'Weet je het zeker? Je kan het niet meer ongedaan maken.',
														positiveLabel:
															'Verwijder voor altijd',
														negativeLabel:
															'Bewaren',
													},
												},
											]}
										/>
									</h2>
								</div>
								{mediaItem.type === 'photo' && (
									<motion.img
										key={mediaItem.id}
										className="max-h-full shrink-0 rounded-md object-cover"
										src={mediaItem.src}
										initial={{
											scale: reduceMotion ? 0.9 : 0.75,
										}}
										animate={{
											scale: 0.9,
											rotate: reduceMotion
												? 0
												: `${mediaItem.rotation}deg`,
										}}
									/>
								)}
								{mediaItem.type === 'audio' && (
									<div className="shrink-0 p-4">
										<AudioPlayer
											key={mediaItem.id}
											src={mediaItem.src}
											mimeType={mediaItem.mime}
										/>
									</div>
								)}
							</li>
						))}
					</ol>
				</motion.div>
			</DialogPanel>
		</Dialog>
	);
};

export default UserMediaGallery;
